import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api, BACKEND, errMsg } from "@/lib/api";
import { Panel, SaveButton } from "@/components/admin/Panel";

const StatusLine = ({ ic }) => {
  if (!ic.import_url) return <p className="text-sm text-muted-foreground" data-testid="sync-status">Not configured</p>;
  if (ic.last_status === "ok") return <p className="text-sm text-[hsl(var(--sage))]" data-testid="sync-status">Last synced {new Date(ic.last_synced_at).toLocaleString()} · {ic.imported_count} event(s) imported. Refreshes automatically every ~20 minutes.</p>;
  if (ic.last_status === "error") return <p className="text-sm text-destructive" data-testid="sync-status">Sync failed: {ic.last_error}</p>;
  return <p className="text-sm text-muted-foreground" data-testid="sync-status">Not yet synced</p>;
};

export const SyncPanel = ({ settings, reload }) => {
  const ic = settings.ical;
  const [url, setUrl] = useState(ic.import_url);
  const [token, setToken] = useState(ic.export_token);
  const [busy, setBusy] = useState("");
  useEffect(() => {
    if (!token) api.post("/admin/ical/export-token").then((r) => setToken(r.data.token));
  }, [token]);
  const run = async (kind) => {
    setBusy(kind);
    try {
      const { data } = kind === "save" ? await api.put("/admin/ical", { import_url: url }) : await api.post("/admin/ical/sync");
      if (data.status === "error") toast.error(`Sync failed: ${data.error}`); else toast.success(data.status === "ok" ? `Synced ${data.imported} event(s)` : "Saved");
      reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const exportUrl = token ? `${BACKEND}/api/ical/${token}.ics` : "";
  return (
    <>
      <Panel title="Import from Airbnb" desc="In Airbnb: Listing → Availability → Connect calendars → Export calendar. Paste that link here. Imported dates become unavailable on this site." testid="sync-import-panel">
        <div className="flex flex-col sm:flex-row gap-3">
          <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.airbnb.com/calendar/ical/....ics" data-testid="ical-import-input" />
          <SaveButton onClick={() => run("save")} busy={busy === "save"} testid="ical-save-button" label="Save & sync" />
          {ic.import_url && <button onClick={() => run("sync")} disabled={!!busy} className="border border-border px-5 rounded-full text-sm" data-testid="ical-sync-now-button">{busy === "sync" ? "Syncing…" : "Sync now"}</button>}
        </div>
        <div className="mt-4"><StatusLine ic={ic} /></div>
      </Panel>
      <Panel title="Export to Airbnb" desc="In Airbnb: Connect calendars → Import calendar, and paste this link so direct bookings and blocked dates also block Airbnb." testid="sync-export-panel">
        <div className="flex gap-3">
          <Input readOnly value={exportUrl} data-testid="ical-export-url" />
          <button onClick={() => { navigator.clipboard.writeText(exportUrl); toast.success("Link copied"); }} className="border border-border px-4 rounded-full" aria-label="Copy link" data-testid="ical-copy-button"><Copy className="w-4 h-4" /></button>
        </div>
        <p className="text-xs text-muted-foreground mt-3">Airbnb refreshes imported calendars on its own schedule (typically every few hours). Keep this link private.</p>
      </Panel>
    </>
  );
};
